/**
 * ComparadorRfq — Ver juntas las cotizaciones que compiten, y elegir una.
 *
 * Duplicar una solicitud para otro proveedor ya existía y las agrupaba con `rfqGroup`,
 * pero verlas juntas no: había que abrir cada una y anotar los totales en un papel. El
 * grupo era una etiqueta sin uso.
 *
 * Señala la más barata y NO la elige. La más barata no es necesariamente la que hay que
 * comprar —plazo, certificados y con quién se puede contar cuentan— así que la pantalla
 * informa y la persona decide.
 *
 * Elegir CANCELA las hermanas, no las borra: las que no se eligieron son la prueba de que
 * se comparó, y sirven la próxima vez que haya que comprar lo mismo.
 */
import React, { useCallback, useEffect, useState } from "react";
import backend from "../../backend";

const clp = n => `$${Math.round(Number(n) || 0).toLocaleString("es-CL")}`;

const ESTADO = {
  cotizando: { texto: "esperando respuesta", color: "#8a8a8a" },
  cotizado:  { texto: "cotizada",            color: "#a78bfa" },
  pedido:    { texto: "comprada",            color: "#2ecc71" },
  enCamino:  { texto: "en camino",           color: "#2ecc71" },
  recibido:  { texto: "recibida",            color: "#2ecc71" },
  cancelado: { texto: "descartada",          color: "#e74c3c" },
};

const ComparadorRfq = ({ rfqGroup, poActualId, onCambio }) => {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");
  const [trabajando, setTrabajando] = useState(false);

  const traer = useCallback(async () => {
    if (!rfqGroup) return;
    try {
      const { data } = await backend.get(`purchaseOrder/rfq/${encodeURIComponent(rfqGroup)}`);
      setDatos(data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  }, [rfqGroup]);

  useEffect(() => { traer(); }, [traer]);

  const elegir = async (id, numero) => {
    const otras = (datos?.cotizaciones || []).filter(
      c => c._id !== id && ["cotizando", "cotizado"].includes(c.status)
    );
    const aviso = otras.length
      ? `Se elige la N°${numero} y se descartan ${otras.length} cotización(es): ${otras.map(o => `N°${o.number}`).join(", ")}.`
      : `Se elige la N°${numero}.`;
    const motivo = window.prompt(`${aviso}\n\n¿Por qué esta? (opcional, queda anotado)`);
    if (motivo === null) return;   // canceló
    setTrabajando(true);
    setError("");
    try {
      await backend.post(`purchaseOrder/${id}/elegir-del-rfq`, { motivo });
      await traer();
      onCambio?.();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setTrabajando(false);
    }
  };

  // Con una sola cotización no hay nada que comparar y el bloque sería ruido.
  if (!rfqGroup || !datos || (datos.cotizaciones || []).length < 2) return null;

  return (
    <div style={{ background: "#111", border: "1px solid #2e2e2e", borderRadius: 6,
                  padding: "10px 12px", marginBottom: 12 }}>
      <div style={{ fontSize: 10, color: "#a78bfa", textTransform: "uppercase",
                    letterSpacing: ".05em", marginBottom: 6 }}>
        Cotizaciones del mismo material
      </div>

      {error && <div style={{ fontSize: 11, color: "#e74c3c", marginBottom: 6 }}>⚠ {error}</div>}

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <tbody>
          {datos.cotizaciones.map(c => {
            const e = ESTADO[c.status] || { texto: c.status, color: "#8a8a8a" };
            const esta = c._id === poActualId;
            return (
              <tr key={c._id} style={{ borderTop: "1px solid #222",
                                       background: esta ? "#1a1a1a" : "transparent" }}>
                <td style={{ padding: "5px 6px", color: "#ddd" }}>
                  N°{c.number}{esta ? " (esta)" : ""}
                </td>
                <td style={{ padding: "5px 6px", color: "#ddd" }}>
                  {c.proveedor || <i style={{ color: "#777" }}>sin proveedor</i>}
                </td>
                <td style={{ padding: "5px 6px", textAlign: "right", color: "#ddd",
                             fontVariantNumeric: "tabular-nums" }}>
                  {c.comparable ? clp(c.total) : "—"}
                  {c.esMasBarata && <span style={{ color: "#2ecc71", marginLeft: 6 }}>más barata</span>}
                </td>
                <td style={{ padding: "5px 6px", color: e.color, fontSize: 11 }}>{e.texto}</td>
                <td style={{ padding: "5px 6px", textAlign: "right" }}>
                  {["cotizando", "cotizado"].includes(c.status) && (
                    <button type="button" disabled={trabajando} onClick={() => elegir(c._id, c.number)}
                      style={{ background: "#2ecc7122", color: "#2ecc71", border: "1px solid #2ecc7155",
                               borderRadius: 4, padding: "2px 9px", fontSize: 10, fontWeight: 700,
                               cursor: "pointer" }}>
                      Elegir
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 6 }}>
        Elegir descarta las otras cotizaciones (no las borra) y deja anotado el motivo.
        Comprar sigue siendo un paso aparte: "Convertir en OC".
      </div>
    </div>
  );
};

export default ComparadorRfq;
